import {
  AppCard,
  Button,
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiContentSaveOutline } from "@mdi/js";
import { Box } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useCreateOtherMasterdataServicesRequest } from "../../../../__generated__/orval/endpoints/other-masterdata-services-requests/other-masterdata-services-requests";
import { OtherMasterdataForm } from "./-internal-components/OtherMasterdataForm";
import {
  type OtherMasterdataSchema,
  otherMasterdataSchema,
} from "./-schemas/otherMasterdataSchema";

const toPreviousTicketId = (value?: string): number | undefined => {
  if (!value) {
    return undefined;
  }

  const id = Number(value);

  return Number.isFinite(id) ? id : undefined;
};

const toMatnumbers = (
  values?: Array<string | { matnumber: string }>,
): string[] | undefined => {
  if (!values?.length) {
    return undefined;
  }

  const matnumbers = values
    .map((value) => (typeof value === "string" ? value : value.matnumber))
    .map((value) => value.trim())
    .filter((value) => value.length > 0);

  return matnumbers.length ? matnumbers : undefined;
};

const OtherMasterdataServicesRequest: React.FC = () => {
  const { t } = useTranslation();

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      backup_requester: null,
      description: "",
      file_attachments: [],
      is_repetro: null,
      matnumber: [],
      previous_ticket: "",
      quantity_of_materials: "",
      requester_field: null,
      subject: "",
    },
    resolver: zodResolver(otherMasterdataSchema),
  });

  const { mutate, isPending } = useCreateOtherMasterdataServicesRequest({
    mutation: {
      onError: () => {
        enqueueSnackbar(t("mmRequestChild.errorMessage"), {
          variant: "error",
        });
      },
      onSuccess: (response) => {
        enqueueSnackbar(t("mmRequestChild.successMessage"), {
          variant: "success",
        });
        window.location.href = `/vbuyer/mm_requests?flash_message=${t("mmRequestChild.flashMessage")}${response.mm_request?.id}`;
      },
    },
  });

  const handleFilesChange = useCallback(
    (fileAttachments: string[]) => {
      setValue("file_attachments", fileAttachments);
    },
    [setValue],
  );

  const onSubmit: SubmitHandler<OtherMasterdataSchema> = useCallback(
    (data) => {
      mutate({
        data: {
          other_masterdata_services_request: {
            is_repetro: data.is_repetro || undefined,
            mm_request_attributes: {
              backup_requester_id: data.backup_requester?.id,
              description: data.description,
              file_attachments: data.file_attachments,
              matnumber: toMatnumbers(data.matnumber),
              previous_ticket_id: toPreviousTicketId(data.previous_ticket),
              quantity_of_materials: data.quantity_of_materials || undefined,
              requester_field: data.requester_field?.name,
              subject: data.subject,
            },
          },
        },
      });
    },
    [mutate],
  );

  return (
    <PageContainer
      backTo="/vbuyer/mm_requests/create_new_requests"
      maxWidth="md"
      showBackButton
      title={t("common.otherMasterdataServicesRequest")}
    >
      <AppCard>
        <OtherMasterdataForm
          control={control}
          onFilesChange={handleFilesChange}
          onSubmit={handleSubmit(onSubmit)}
          setValue={setValue}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              color="success"
              loading={isPending}
              startIcon={<Icon icon={mdiContentSaveOutline} />}
              type="submit"
            >
              {t("common.save")}
            </Button>
          </Box>
        </OtherMasterdataForm>
      </AppCard>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/other_masterdata_services_requests/new",
)({
  component: OtherMasterdataServicesRequest,
});
